import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { FieldValidationState } from '../../../features/users/users.model';
import { UsersService } from '../../../features/users/users.service';

@Component({
  selector: 'app-phone-input',
  standalone: false,
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss'
})
export class PhoneInputComponent implements OnInit, OnDestroy {
  @Input() value: string = '';
  @Input() validationState: FieldValidationState = { status: 'idle' };
  @Input() originalValue?: string;
  @Output() valueChange = new EventEmitter<string>();
  @Output() validationStateChange = new EventEmitter<FieldValidationState>();

  private inputSubject = new Subject<string>();
  private sub?: Subscription;

  constructor(private usersService: UsersService) { }

  ngOnInit(): void {
    this.sub = this.inputSubject.pipe(
      debounceTime(500),
      switchMap((val) => {
        if (!val) {
          this.setValidationState({ status: 'idle' });
          return of(null);
        }
        if (this.originalValue !== undefined && val === this.originalValue) {
          this.setValidationState({ status: 'valid' });
          return of(null);
        }
        if (!this.isValidPhone(val)) {
          this.setValidationState({
            status: 'error',
            message: 'Phone must include country code (e.g. +212)'
          });
          return of(null);
        }
        // Valid, proceed with async check
        this.setValidationState({ status: 'checking' });
        return this.usersService.checkPhoneUniqueness(val);
      })
    ).subscribe({
      next: (res) => {
        if (!res) return;
        if (res.available) {
          this.setValidationState({ status: 'valid', message: '✓ Available' });
        } else {
          this.setValidationState({
            status: 'conflict',
            message: `Phone ${this.value} was just registered to another user (created ${res.secondsAgo || 0} seconds ago). Your form data has been preserved.`
          });
        }
      },
      error: (err) => {
        this.setValidationState({ status: 'error', message: 'Error checking phone uniqueness' });
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const val = target.value;
    this.value = val;
    this.valueChange.emit(val);

    if (!val) {
      this.setValidationState({ status: 'idle' });
    } else if (this.originalValue !== undefined && val === this.originalValue) {
      this.setValidationState({ status: 'valid' });
    } else if (!this.isValidPhone(val)) {
      this.setValidationState({
        status: 'error',
        message: 'Phone must include country code (e.g. +212)'
      });
    } else {
      this.setValidationState({ status: 'checking' });
      this.inputSubject.next(val);
    }
  }

  private setValidationState(state: FieldValidationState): void {
    this.validationState = state;
    this.validationStateChange.emit(state);
  }

  private isValidPhone(phone: string): boolean {
    // Must start with + followed by at least country code and numbers
    // Let's accept space formatting, but must start with + followed by digit
    const trimmed = phone.trim();
    return trimmed.startsWith('+') && trimmed.length >= 7;
  }
}
